import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { authService } from '@/services/auth.service';
import { catalogService } from '@/services/catalog.service';
import { profileService } from '@/services/profile.service';
import { useAuthStore } from '@/stores/auth.store';
import { getInitData, getTelegram } from '@/shared/lib/telegram';
import { useT } from '@/i18n';
import { Button } from '@/shared/ui/Button';
import { extractErrorMessage } from '@/shared/lib/api';

export function SplashPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const updateUser = useAuthStore((s) => s.updateUser);
  const token = useAuthStore((s) => s.accessToken);
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Where to go after a successful login. Three sources, in priority order:
  //   1. PrivateRoute redirected here from a protected URL → location.state.from
  //   2. Bot deep link: Telegram exposes ?startapp=... as initDataUnsafe.start_param
  //      (we map common slugs → /category/<slug>)
  //   3. Default → /home
  const resolveTarget = (): string => {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)
      ?.from?.pathname;
    if (from && from !== '/') return from;
    const sp = getTelegram()?.initDataUnsafe?.start_param;
    if (sp && /^[a-z0-9-]{2,40}$/i.test(sp)) return `/category/${sp}`;
    return '/home';
  };

  useEffect(() => {
    if (token) {
      navigate(resolveTarget(), { replace: true });
      // Refresh the cached profile in the background so admin-side changes
      // (e.g. lifting a restriction) take effect on the very next app open —
      // not only after the 24h token expires. Failures are ignored; the
      // persisted user stays as a fallback.
      void profileService
        .getMe()
        .then((u) => updateUser(u))
        .catch(() => undefined);
      return;
    }
    const tg = getTelegram();
    const initData = getInitData();
    if (!tg || !initData) {
      setLoading(false);
      setError(t('splash.not_in_tg'));
      return;
    }

    // Tell Telegram WebApp we're ready ASAP so it stops showing its splash.
    try {
      tg.ready?.();
      tg.expand?.();
    } catch {
      // Older clients may not implement these — ignore.
    }

    authService
      .loginWithTelegram(initData)
      .then((res) => {
        setSession(res.accessToken, res.user);
        // Warm the HomePage queries in parallel — by the time React renders
        // /home, categories + featured are already in the React Query cache.
        // Failures are fine (HomePage will re-fetch).
        void qc.prefetchQuery({
          queryKey: ['categories'],
          queryFn: catalogService.listCategories,
          staleTime: 30_000,
        });
        void qc.prefetchQuery({
          queryKey: ['products', 'featured'],
          queryFn: () => catalogService.listProducts({ featured: true, limit: 10 }),
          staleTime: 30_000,
        });
        navigate(resolveTarget(), { replace: true });
      })
      .catch((err) => {
        setLoading(false);
        setError(extractErrorMessage(err, t('common.error')));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg-primary flex flex-col items-center justify-center px-6 text-center">
      {/* Decorative gradient blobs */}
      <div className="absolute -top-32 -right-24 w-[26rem] h-[26rem] rounded-full bg-[#7F77DD]/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-24 w-[24rem] h-[24rem] rounded-full bg-[#D4537E]/20 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[20rem] h-[20rem] rounded-full bg-[#2AABEE]/15 blur-3xl pointer-events-none" />

      {/* Logo */}
      <div className="relative mb-7">
        {/* Outer rotating conic ring */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
          className="absolute -inset-2.5 rounded-full"
          style={{
            background:
              'conic-gradient(from 0deg, #D4537E, #7F77DD, #2AABEE, #00FF8C, #D4537E)',
            mask: 'radial-gradient(circle, transparent 60%, black 62%)',
            WebkitMask: 'radial-gradient(circle, transparent 60%, black 62%)',
          }}
        />

        {/* Pulsing halo */}
        <motion.div
          animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.2, 0.55] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(0,255,140,0.45), transparent 70%)',
            filter: 'blur(14px)',
          }}
        />

        {/* Main logo badge */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0, rotate: -12 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 16 }}
          className="relative h-28 w-28 rounded-full flex items-center justify-center"
          style={{
            background:
              'linear-gradient(135deg, #2AABEE 0%, #7F77DD 55%, #D4537E 100%)',
            boxShadow:
              'inset 0 3px 0 rgba(255,255,255,0.3), inset 0 -3px 0 rgba(0,0,0,0.25), 0 14px 36px rgba(0,0,0,0.55)',
          }}
        >
          {/* Floating animation */}
          <motion.div
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            className="flex items-center justify-center"
          >
            {/* Checkmark SVG (galochka) */}
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <motion.path
                d="M12 28 L24 40 L44 16"
                stroke="#fff"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.6, delay: 0.25, ease: [0.2, 0.7, 0.2, 1] }}
                style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.85))' }}
              />
            </svg>
          </motion.div>
        </motion.div>
      </div>

      {/* Brand title with gradient text */}
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="text-[34px] font-extrabold tracking-tight leading-none mb-2"
        style={{
          background:
            'linear-gradient(180deg, #FFFFFF 0%, #C4B5FD 60%, #A78BFA 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          filter: 'drop-shadow(0 4px 12px rgba(167,139,250,0.5))',
        }}
      >
        Galochka Bot
      </motion.h1>

      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="text-text-secondary text-[13.5px] mb-10 max-w-[18rem]"
      >
        {t('splash.subtitle')}
      </motion.p>

      {/* Loading state — creative multi-stage progress so the user has
          something to read instead of a flat spinner on slow connections */}
      {loading && <CreativeLoader />}

      {/* Error state */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: 1,
            y: 0,
            x: [0, -6, 6, -4, 4, 0],
          }}
          transition={{
            opacity: { duration: 0.32 },
            y: { duration: 0.32 },
            x: { delay: 0.1, duration: 0.45 },
          }}
          className="w-full max-w-xs"
        >
          <div
            className="mb-4 rounded-2xl px-4 py-3 flex items-start gap-2.5 ring-1 ring-danger/30"
            style={{
              background: 'rgba(255,69,58,0.08)',
              boxShadow: '0 8px 22px rgba(255,69,58,0.18)',
            }}
          >
            <span className="sticker-glow text-[16px] leading-none shrink-0 mt-0.5">⚠️</span>
            <p className="text-[12.5px] text-left text-danger/95 leading-snug">
              {error}
            </p>
          </div>
          <Button fullWidth size="lg" onClick={() => window.location.reload()}>
            🔄 {t('common.retry')}
          </Button>
        </motion.div>
      )}

      {/* Footer brand strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        className="absolute bottom-6 inset-x-0 flex justify-center items-center gap-1.5"
      >
        <span className="h-1 w-1 rounded-full bg-success animate-pulse shadow-[0_0_6px_rgba(0,255,140,0.7)]" />
        <span className="text-text-tertiary text-[10px] uppercase tracking-[0.18em] font-semibold">
          Powered by Galochka
        </span>
      </motion.div>
    </div>
  );
}

/* ===================== Creative loader ===================== */
/**
 * Multi-stage progress + rotating tip carousel. Designed for slow Uzbek mobile
 * connections — gives the user something engaging to watch (and learn from)
 * instead of staring at a static "Loading...".
 *
 * Three pieces stacked vertically:
 *   1. Stage chip — fakes a progression (Ulanish → Tasdiqlash → Yuklash) so the
 *      user feels something is happening even when the server is slow.
 *   2. Animated dot bar — branded RGB pulse like the original, kept as visual
 *      anchor.
 *   3. Tip carousel — short, useful tagline that cycles every 2.6 s. Reduces
 *      perceived wait time and educates new users.
 */
function CreativeLoader() {
  const t = useT();
  const lang = useAuthStore.getState().language as 'uz' | 'ru' | 'en';
  const tipL = (uz: string, ru: string, en: string) =>
    lang === 'ru' ? ru : lang === 'en' ? en : uz;

  // Fake stage progression — the real auth is happening in parallel; this
  // just gives the user a feeling of progress on a connection where the
  // round-trip might take 3-5 s.
  const stages = useMemo(
    () => [
      { key: 'connect', icon: '🌐', label: tipL('Ulanish...', 'Подключение...', 'Connecting...') },
      { key: 'auth', icon: '🔐', label: tipL('Tasdiqlash...', 'Авторизация...', 'Authenticating...') },
      { key: 'load', icon: '✨', label: tipL('Yuklash...', 'Загрузка...', 'Loading...') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang],
  );
  const [stageIdx, setStageIdx] = useState(0);
  useEffect(() => {
    if (stageIdx >= stages.length - 1) return;
    const timer = setTimeout(() => setStageIdx((i) => i + 1), 1100);
    return () => clearTimeout(timer);
  }, [stageIdx, stages.length]);

  // Rotating tips — pulls from a localised list, advances every 2.6 s.
  const tips = useMemo(
    () => [
      tipL(
        '⚡ Buyurtmalar 5–15 daqiqada avtomatik faollashadi',
        '⚡ Заказы активируются автоматически за 5–15 минут',
        '⚡ Orders activate automatically in 5–15 minutes',
      ),
      tipL(
        '🛡 Toʻlovlaringiz 100% himoyada — bank kartalari saqlanmaydi',
        '🛡 Платежи 100% защищены — карты не сохраняются',
        '🛡 Payments are 100% safe — we never store card data',
      ),
      tipL(
        '🌟 Instagram Meta Verified — soxta akkauntlardan himoya',
        '🌟 Instagram Meta Verified — защита от фейков',
        '🌟 Instagram Meta Verified — protection from impostors',
      ),
      tipL(
        '🎁 Doʻstingizni taklif qiling — har xaridining 5% ini oling',
        '🎁 Пригласите друга — получите 5% с его покупок',
        '🎁 Invite a friend — earn 5% of their purchases',
      ),
      tipL(
        '📞 24/7 yordam: har qanday savol bilan murojaat qiling',
        '📞 Поддержка 24/7: любой вопрос — мы рядом',
        '📞 24/7 support — drop us a line any time',
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang],
  );
  const [tipIdx, setTipIdx] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTipIdx((x) => (x + 1) % tips.length), 2600);
    return () => clearInterval(i);
  }, [tips.length]);

  const stage = stages[stageIdx];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.05, duration: 0.22 }}
      className="flex flex-col items-center gap-3 w-full max-w-xs"
    >
      {/* Stage chip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={stage.key}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/90"
          style={{
            background:
              'linear-gradient(135deg, rgba(127,119,221,0.18), rgba(42,171,238,0.10))',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
          }}
        >
          <span className="text-[14px] leading-none">{stage.icon}</span>
          <span>{stage.label}</span>
        </motion.div>
      </AnimatePresence>

      {/* Branded RGB dot loader (kept from original) */}
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="h-2.5 w-2.5 rounded-full"
            style={{
              background: i === 0 ? '#2AABEE' : i === 1 ? '#7F77DD' : '#D4537E',
              boxShadow: '0 0 10px currentColor',
              color: i === 0 ? '#2AABEE' : i === 1 ? '#7F77DD' : '#D4537E',
            }}
            animate={{ y: [0, -8, 0], opacity: [0.5, 1, 0.5] }}
            transition={{
              duration: 0.9,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.15,
            }}
          />
        ))}
      </div>

      {/* Slim progress bar */}
      <div
        className="relative h-[3px] w-full rounded-full overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.06)' }}
      >
        <motion.span
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: '40%',
            background:
              'linear-gradient(90deg, #2AABEE, #7F77DD, #D4537E, #2AABEE)',
            boxShadow: '0 0 12px rgba(127,119,221,0.6)',
          }}
          animate={{ x: ['-100%', '250%'] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Rotating tip carousel — fades in/out so it never feels static */}
      <div className="mt-1 h-9 relative w-full">
        <AnimatePresence mode="wait">
          <motion.p
            key={tipIdx}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.32 }}
            className="absolute inset-x-0 top-0 text-center text-[11.5px] text-white/65 leading-snug px-3"
          >
            {tips[tipIdx]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Tiny dot indicator for which tip you're on */}
      <div className="flex items-center gap-1 -mt-1">
        {tips.map((_, i) => (
          <span
            key={i}
            className="h-1 w-1 rounded-full transition-all"
            style={{
              background:
                i === tipIdx ? '#A78BFA' : 'rgba(255,255,255,0.18)',
              boxShadow: i === tipIdx ? '0 0 6px rgba(167,139,250,0.7)' : undefined,
              width: i === tipIdx ? 10 : 4,
            }}
          />
        ))}
      </div>

      {/* Fallback retry hint after long wait */}
      <SlowConnectionHint t={t} lang={lang} />
    </motion.div>
  );
}

/** Appears only if loading drags past 8s — gives the user something to do. */
function SlowConnectionHint({
  t,
  lang,
}: {
  t: (k: string) => string;
  lang: 'uz' | 'ru' | 'en';
}) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const i = setTimeout(() => setShow(true), 8000);
    return () => clearTimeout(i);
  }, []);
  if (!show) return null;
  const label =
    lang === 'ru'
      ? 'Соединение медленное. Подождите или нажмите для повтора.'
      : lang === 'en'
      ? "Connection is slow. Hold on, or tap to retry."
      : 'Internet sekin ekan. Kuting yoki bosib qayta urinib koʻring.';
  return (
    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={() => window.location.reload()}
      className="mt-3 px-3 py-1.5 rounded-full text-[10.5px] font-semibold text-white/75 hover:text-white transition-colors"
      style={{
        background: 'rgba(255,255,255,0.04)',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
      }}
    >
      🐢 {label} ({t('common.retry')})
    </motion.button>
  );
}
